import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Scene from './scenes/Scene';
import SceneContent from './scenes/SceneContent';
import NavigationHint from './ui/NavigationHint';
import ProgressIndicator from './ui/ProgressIndicator';
import AmbientBackground from './ui/AmbientBackground';
import VaultenWordmark from './ui/VaultenWordmark';
import PathChoice from './ui/PathChoice';
import RoomEndChoice from './ui/RoomEndChoice';
import OpeningScreen from './OpeningScreen';
import ClosingScreen from './ClosingScreen';
import FeelInteractive from './FeelInteractive';
import WeighInteractive from './WeighInteractive';
import TranslateInteractive from './TranslateInteractive';
import StabilizeAnchor from './StabilizeAnchor';

const scenes = [
  // Phase 0: Arrival
  {
    id: 1,
    title: 'Arrival',
    subtitle: 'Stillness',
    lines: [
      'You have arrived.',
      'Nothing is required of you here.',
      'Your presence is enough.',
      'This space exists to hold what already knows its shape.'
    ]
  },
  {
    id: 2,
    title: 'Orientation',
    subtitle: 'Subtle Focus',
    lines: [
      'Vaulten is a place for what has been decided inwardly,',
      'even if it has not been spoken.',
      'Here, clarity comes before language.',
      'Weight is felt before action.',
      'Distance forms without force.'
    ]
  },
  {
    id: 3,
    title: 'Centering',
    subtitle: 'Containment Begins',
    lines: [
      'Bring to mind the matter that brought you here.',
      'Do not explain it.',
      'Do not justify it.',
      'Simply allow it to be present.'
    ]
  },
  {
    id: 4,
    title: 'Declaration',
    subtitle: 'Silent Authority',
    lines: [
      'What you hold here does not need permission.',
      'It does not need agreement.',
      'It rests within you, intact.',
      'Vaulten exists to keep it steady.'
    ]
  },
  {
    id: 5,
    title: 'Transition',
    subtitle: 'Threshold',
    lines: [
      'When you are ready,',
      'we will look at what is being held,',
      'what it carries,',
      'and whether it asks for words at all.',
      'For now,',
      'remain here.'
    ]
  },
  // Phase I: Internal Severance
  {
    id: 6,
    title: 'Stillness Holds',
    subtitle: 'Subtle Shift',
    phase: 'I',
    lines: [
      'What you brought here has weight.',
      'Not all weight asks to be carried.',
      'Some weight asks to be set down,',
      'not outwardly,',
      'but inwardly.'
    ]
  },
  {
    id: 7,
    title: 'Recognition',
    subtitle: 'Quiet Differentiation',
    phase: 'I',
    lines: [
      'There is a difference between what you choose',
      'and what you tolerate.',
      'Notice where this matter sits in you.',
      'Is it aligned with what you are willing to hold,',
      'or has it been resting on you without invitation?',
      'No answer is required.',
      'Awareness is sufficient.'
    ]
  },
  {
    id: 8,
    title: 'Internal Boundary Forms',
    subtitle: 'Containment Tightens',
    phase: 'I',
    lines: [
      'Within you, a line already exists.',
      'It does not need to be drawn.',
      'It does not need to be defended.',
      'It is simply where your consent ends.',
      'Let that line become clear.'
    ]
  },
  {
    id: 9,
    title: 'Severance Without Action',
    subtitle: 'Authority Settles',
    phase: 'I',
    lines: [
      'You are not asked to change anything.',
      'Nothing moves outward from here.',
      'Yet something settles inwardly.',
      'What does not belong beyond this line',
      'no longer asks for your energy.',
      'It is no longer being carried by you.'
    ]
  },
  {
    id: 10,
    title: 'Holding State',
    subtitle: 'Vault Active',
    phase: 'I',
    lines: [
      'This is the moment Vaulten exists for.',
      'What you continue to hold is held by choice.',
      'What you do not is released without conflict.',
      'Both are complete.',
      'Remain here long enough to feel the difference.'
    ],
    isHoldingState: true
  },
  {
    id: 11,
    phase: 'I',
    lines: [],
    isPathChoice: true
  },
  // FEEL - A room, not an option
  {
    id: 12,
    phase: 'Feel',
    lines: [
      'You are here with what is being held.',
      '',
      'Nothing is asked of it.',
      'Nothing is asked of you.',
      '',
      'Let your attention rest where this matter lives.'
    ],
    isFeelRoom: true,
    feelPart: 1
  },
  {
    id: 13,
    phase: 'Feel',
    lines: [
      'Some things settle in the chest.',
      'Some in the throat.',
      'Some lower, deeper, quieter.',
      '',
      'Allow its place to be known.'
    ],
    isFeelRoom: true,
    feelPart: 2
  },
  {
    id: 14,
    phase: 'Feel',
    lines: [
      'Notice its texture.',
      '',
      'Is it tight or open.',
      'Heavy or light.',
      'Still or restless.',
      '',
      'There is no need to name it.'
    ],
    isFeelRoom: true,
    feelPart: 3
  },
  {
    id: 15,
    phase: 'Feel',
    lines: [
      'Every boundary has a form.',
      '',
      'It may feel like a line.',
      'A wall.',
      'A distance.',
      'A closing.',
      '',
      'Let the form appear in its own way.'
    ],
    isFeelRoom: true,
    feelPart: 4
  },
  {
    id: 16,
    phase: 'Feel',
    lines: [
      'This boundary exists to protect something.',
      '',
      'Notice what it protects.',
      'Time.',
      'Energy.',
      'Body.',
      'Peace.',
      'Dignity.',
      'Future.',
      '',
      'One is enough.'
    ],
    isFeelRoom: true,
    feelPart: 5
  },
  {
    id: 17,
    phase: 'Feel',
    lines: [
      'Stay here.',
      '',
      'Vaulten holds.'
    ],
    isFeelRoom: true,
    feelPart: 6,
    isRoomEnd: true,
    exitOptions: ['weigh', 'translate', 'close']
  },
  // WEIGH - A room for understanding the weight of what is held
  {
    id: 18,
    phase: 'Weigh',
    lines: [
      'What you are holding has weight.',
      '',
      'Not all weight is the same.',
      '',
      'Some weight strengthens.',
      'Some weight drains.',
      'Some weight becomes heavy only over time.',
      '',
      'Let this one be felt honestly.'
    ],
    isWeighRoom: true,
    weighPart: 1
  },
  {
    id: 19,
    phase: 'Weigh',
    lines: [
      'Notice what holding this boundary costs.',
      '',
      'Energy.',
      'Time.',
      'Attention.',
      'Opportunity.',
      'Connection.',
      '',
      'There is no need to total it.',
      'Only to recognize it.'
    ],
    isWeighRoom: true,
    weighPart: 2
  },
  {
    id: 20,
    phase: 'Weigh',
    lines: [
      'Now notice what it preserves.',
      '',
      'Rest.',
      'Focus.',
      'Safety.',
      'Integrity.',
      'Continuity.',
      'Self-respect.',
      '',
      'One is enough.'
    ],
    isWeighRoom: true,
    weighPart: 3
  },
  {
    id: 21,
    phase: 'Weigh',
    lines: [
      'Weight is not a judgment.',
      'It is information.'
    ],
    isWeighRoom: true,
    weighPart: 4
  },
  {
    id: 22,
    phase: 'Weigh',
    lines: [
      'Imagine carrying this boundary as it is,',
      'not today,',
      'but over time.',
      '',
      'Does it feel sustainable.',
      'Or does it ask to be adjusted.',
      '',
      'Both answers are valid.'
    ],
    isWeighRoom: true,
    weighPart: 5
  },
  {
    id: 23,
    phase: 'Weigh',
    lines: [
      'A boundary that cannot be carried',
      'eventually collapses.',
      '',
      'A boundary that fits',
      'settles quietly into place.'
    ],
    isWeighRoom: true,
    weighPart: 6
  },
  {
    id: 24,
    phase: 'Weigh',
    lines: [
      'Stay here long enough',
      'to know the difference.',
      '',
      'Vaulten holds.'
    ],
    isWeighRoom: true,
    weighPart: 7,
    isRoomEnd: true,
    exitOptions: ['feel', 'translate', 'close']
  },
  // TRANSLATE - A room for finding clear language
  {
    id: 25,
    phase: 'Translate',
    lines: [
      'What you are holding may ask for words.',
      '',
      'Not to convince.',
      'Not to explain.',
      'Not to be understood.',
      '',
      'Only to be clear.'
    ],
    isTranslateRoom: true,
    translatePart: 1
  },
  {
    id: 26,
    phase: 'Translate',
    lines: [
      'Language here is not a bridge.',
      'It is a boundary.',
      '',
      'Let the words be simple.',
      'Let them be few.'
    ],
    isTranslateRoom: true,
    translatePart: 2
  },
  {
    id: 27,
    phase: 'Translate',
    lines: [
      'Notice the difference between',
      'stating',
      'and defending.',
      '',
      'Defending invites negotiation.',
      'Stating completes the matter.'
    ],
    isTranslateRoom: true,
    translatePart: 3
  },
  {
    id: 28,
    phase: 'Translate',
    lines: [
      'Begin with what is true,',
      'without history,',
      'without justification.',
      '',
      '"This is what I am able to do."',
      '"This is what I am not available for."',
      '"This is where I am stopping."',
      '"This is what I will continue."',
      '',
      'Choose the smallest sentence',
      'that still holds.'
    ],
    isTranslateRoom: true,
    translatePart: 4
  },
  {
    id: 29,
    phase: 'Translate',
    lines: [
      'If more words are required,',
      'the boundary is not yet settled.'
    ],
    isTranslateRoom: true,
    translatePart: 5
  },
  {
    id: 30,
    phase: 'Translate',
    lines: [
      'Let the language rest',
      'until it feels closed,',
      'not sharp,',
      'not apologetic.',
      '',
      'A clear boundary',
      'does not need force.',
      '',
      'It stands on its own.'
    ],
    isTranslateRoom: true,
    translatePart: 6
  },
  {
    id: 31,
    phase: 'Translate',
    lines: [
      'Stay here',
      'until the words feel finished.',
      '',
      'Vaulten holds.'
    ],
    isTranslateRoom: true,
    translatePart: 7,
    isRoomEnd: true,
    exitOptions: ['close', 'feel', 'weigh']
  },
  // CLOSING RITUAL
  {
    id: 32,
    phase: 'Close',
    lines: [
      'What has been held remains held.',
      '',
      'Nothing needs to be carried further than this moment.'
    ],
    isClosingRitual: true,
    closePart: 1
  },
  {
    id: 33,
    phase: 'Close',
    lines: [
      'The boundary is intact.',
      'The weight is known.',
      'The words, if any, are complete.',
      '',
      'There is nothing left to resolve here.'
    ],
    isClosingRitual: true,
    closePart: 2
  },
  {
    id: 34,
    phase: 'Close',
    lines: [
      'Vaulten does not follow you.',
      'It does not remind you.',
      'It does not watch.',
      '',
      'It exists only to hold what you have already claimed.'
    ],
    isClosingRitual: true,
    closePart: 3
  },
  {
    id: 35,
    phase: 'Close',
    lines: [
      'As you leave,',
      'you take only what is yours.',
      '',
      'Everything else stays here.'
    ],
    isClosingRitual: true,
    closePart: 4
  },
  {
    id: 36,
    phase: 'Close',
    lines: [
      'When you are ready,',
      'you may go.',
      '',
      'Vaulten closes.'
    ],
    isClosingRitual: true,
    closePart: 5,
    isFinalClose: true
  }
];

// Trigger device haptics if available
const triggerHapticPulse = () => {
  if ('vibrate' in navigator) {
    navigator.vibrate(10); // Single soft pulse
  }
};

const VaultenExperience = () => {
  const [showOpening, setShowOpening] = useState(true);
  const [currentScene, setCurrentScene] = useState(0);
  const [showNavigation, setShowNavigation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showPathChoice, setShowPathChoice] = useState(false);
  const [skippedToEnd, setSkippedToEnd] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [showClosing, setShowClosing] = useState(false);
  const [showFeelInteractive, setShowFeelInteractive] = useState(false);
  const [showWeighInteractive, setShowWeighInteractive] = useState(false);
  const [showTranslateInteractive, setShowTranslateInteractive] = useState(false);
  const [showStabilize, setShowStabilize] = useState(false);
  const touchStartRef = useRef(null);
  const containerRef = useRef(null);

  // Handle opening screen completion
  const handleOpeningComplete = useCallback(() => {
    setShowOpening(false);
  }, []);

  // Handle closing screen restart
  const handleClosingRestart = useCallback(() => {
    setShowClosing(false);
    setShowOpening(true);
    setCurrentScene(0);
    setSelectedPath(null);
    setShowPathChoice(false);
    setHasInteracted(false);
    setSkippedToEnd(false);
    setShowFeelInteractive(false);
    setShowWeighInteractive(false);
    setShowTranslateInteractive(false);
    setShowStabilize(false);
  }, []);

  // Handle Feel interactive exit
  const handleFeelExit = useCallback((choice) => {
    setShowFeelInteractive(false);
    setIsTransitioning(true);
    
    if (choice === 'close') {
      // Go to Closing Ritual (index 31)
      setCurrentScene(31);
      setSelectedPath('close');
    } else if (choice === 'weigh') {
      // Show interactive Weigh experience
      setShowWeighInteractive(true);
      setSelectedPath('weigh');
      setIsTransitioning(false);
      return;
    } else if (choice === 'translate') {
      // Show interactive Translate experience
      setShowTranslateInteractive(true);
      setSelectedPath('translate');
      setIsTransitioning(false);
      return;
    }
    
    setTimeout(() => setIsTransitioning(false), 3000);
  }, []);

  // Handle Weigh interactive exit
  const handleWeighExit = useCallback((choice) => {
    setShowWeighInteractive(false);
    setIsTransitioning(true);
    
    if (choice === 'close') {
      // Go to Closing Ritual (index 31)
      setCurrentScene(31);
      setSelectedPath('close');
    } else if (choice === 'feel') {
      // Show interactive Feel experience
      setShowFeelInteractive(true);
      setSelectedPath('feel');
      setIsTransitioning(false);
      return;
    } else if (choice === 'translate') {
      // Show interactive Translate experience
      setShowTranslateInteractive(true);
      setSelectedPath('translate');
      setIsTransitioning(false);
      return;
    }
    
    setTimeout(() => setIsTransitioning(false), 3000);
  }, []);
  // Handle Translate interactive completion - automatic transition to Stabilize + Anchor
  const handleTranslateComplete = useCallback(() => {
    setShowTranslateInteractive(false);
    // After Translate, automatically show Stabilize + Anchor (both phases)
    setShowStabilize(true);
  }, []);

  // Handle Stabilize/Anchor completion - proceed directly to Close
  const handleStabilizeComplete = useCallback(() => {
    setShowStabilize(false);
    // After Stabilize + Anchor, go directly to Close - no branching
    setShowClosing(true);
  }, []);

  // Navigation functions defined first
  const goToNextScene = useCallback(() => {
    // Check if we're at the final close scene
    if (scenes[currentScene].isFinalClose) {
      setShowClosing(true);
      return;
    }
    
    if (currentScene < scenes.length - 1 && !isTransitioning) {
      setIsTransitioning(true);
      setHasInteracted(true);
      setCurrentScene(prev => prev + 1);
      triggerHapticPulse();
      setTimeout(() => setIsTransitioning(false), 3000);
    }
  }, [currentScene, isTransitioning]);

  const goToPrevScene = useCallback(() => {
    if (currentScene > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setHasInteracted(true);
      setCurrentScene(prev => prev - 1);
      triggerHapticPulse();
      setTimeout(() => setIsTransitioning(false), 3000);
    }
  }, [currentScene, isTransitioning]);

  const goToScene = useCallback((index) => {
    if (index !== currentScene && !isTransitioning) {
      setIsTransitioning(true);
      setHasInteracted(true);
      setCurrentScene(index);
      triggerHapticPulse();
      setTimeout(() => setIsTransitioning(false), 3000);
    }
  }, [currentScene, isTransitioning]);

  // Show navigation hint after initial delay (5-8 seconds as specified)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNavigation(true);
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  // Show path choice on final scene (Scene 11) after content appears
  // Or immediately if user skipped
  useEffect(() => {
    if (scenes[currentScene].isPathChoice) {
      let timer;
      if (skippedToEnd) {
        // Skipped - show after a small delay to allow animation to complete
        timer = setTimeout(() => {
          setShowPathChoice(true);
        }, 500);
      } else {
        // Normal flow - shorter delay (was 12000)
        timer = setTimeout(() => {
          setShowPathChoice(true);
        }, 4000);
      }
      return () => clearTimeout(timer);
    }
  }, [currentScene, skippedToEnd]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTransitioning) return;
      
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        goToNextScene();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevScene();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextScene, goToPrevScene, isTransitioning]);

  // Touch/swipe navigation
  const handleTouchStart = useCallback((e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current || isTransitioning) return;
    
    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY,
      time: Date.now()
    };
    
    const deltaX = touchEnd.x - touchStartRef.current.x;
    const deltaY = touchEnd.y - touchStartRef.current.y;
    const deltaTime = touchEnd.time - touchStartRef.current.time;
    
    // Minimum swipe distance and maximum time for valid swipe
    const minSwipeDistance = 50;
    const maxSwipeTime = 500;
    
    if (Math.abs(deltaX) > Math.abs(deltaY) && deltaTime < maxSwipeTime) {
      if (deltaX < -minSwipeDistance) {
        // Swipe left - next scene
        goToNextScene();
      } else if (deltaX > minSwipeDistance) {
        // Swipe right - previous scene
        goToPrevScene();
      }
    }
    
    touchStartRef.current = null;
  }, [goToNextScene, goToPrevScene, isTransitioning]);

  // Handle path selection - navigate to the room
  const handlePathSelect = useCallback((path, sceneIndex) => {
    if (path === 'feel') {
      // Show interactive Feel experience
      setShowFeelInteractive(true);
      setSelectedPath('feel');
      return;
    }
    
    if (path === 'weigh') {
      // Show interactive Weigh experience
      setShowWeighInteractive(true);
      setSelectedPath('weigh');
      return;
    }
    
    if (path === 'translate') {
      // Show interactive Translate experience
      setShowTranslateInteractive(true);
      setSelectedPath('translate');
      return;
    }
    
    if (sceneIndex !== null) {
      // Navigate to the room
      setIsTransitioning(true);
      setCurrentScene(sceneIndex);
      setSelectedPath(path);
      triggerHapticPulse();
      setTimeout(() => setIsTransitioning(false), 3000);
    } else {
      // Room not yet implemented
      setSelectedPath(path);
      triggerHapticPulse();
      console.log(`Path selected: ${path} (room not yet built)`);
    }
  }, []);

  // Handle room end choices (Feel, Weigh, Translate, Close)
  const handleRoomEndSelect = useCallback((choice) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    setIsTransitioning(true);
    
    if (choice === 'close') {
      // Go to Closing Ritual (index 31)
      setCurrentScene(31);
      setSelectedPath('close');
    } else if (choice === 'feel') {
      // Show interactive Feel experience
      setShowFeelInteractive(true);
      setSelectedPath('feel');
      setIsTransitioning(false);
      return;
    } else if (choice === 'weigh') {
      // Show interactive Weigh experience
      setShowWeighInteractive(true);
      setSelectedPath('weigh');
      setIsTransitioning(false);
      return;
    } else if (choice === 'translate') {
      // Show interactive Translate experience
      setShowTranslateInteractive(true);
      setSelectedPath('translate');
      setIsTransitioning(false);
      return;
    }
    
    setTimeout(() => setIsTransitioning(false), 3000);
  }, []);

  // Handle skip intro - go directly to path choices (Scene 11, index 10)
  const handleSkipIntro = useCallback(() => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setHasInteracted(true);
      setSkippedToEnd(true); // Mark that we skipped
      setCurrentScene(10); // Go to path choice scene (index 10)
      triggerHapticPulse();
      setTimeout(() => setIsTransitioning(false), 3000);
    }
  }, [isTransitioning]);

  // Handle click on main area to advance (only after navigation is shown)
  const handleMainClick = useCallback((e) => {
    // Don't advance if clicking on a button or interactive element
    if (e.target.closest('button') || e.target.closest('a')) return;
    // Don't advance on path choice scene
    if (scenes[currentScene].isPathChoice) return;
    // Only advance if navigation is shown
    if (showNavigation) {
      goToNextScene();
    }
  }, [showNavigation, currentScene, goToNextScene]);

  return (
    <>
      {/* Opening Screen */}
      <AnimatePresence>
        {showOpening && (
          <OpeningScreen onComplete={handleOpeningComplete} />
        )}
      </AnimatePresence>

      {/* Main Experience */}
      <div 
        ref={containerRef}
        className="relative min-h-screen bg-background overflow-hidden select-none cursor-pointer"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleMainClick}
      >
        {/* Ambient background effect */}
        <AmbientBackground currentScene={currentScene} totalScenes={scenes.length} />

        {/* Vaulten wordmark - top center */}
      <VaultenWordmark visible={showNavigation} />

      {/* Skip intro - only on first scene, appears with navigation */}
      <AnimatePresence>
        {currentScene === 0 && showNavigation && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2, delay: 1 }}
            onClick={handleSkipIntro}
            className="fixed top-8 right-8 z-20 group"
          >
            <span 
              className="text-xs sm:text-sm tracking-[0.2em] uppercase font-body opacity-50 hover:opacity-70 transition-opacity duration-500"
              style={{ color: 'hsl(40, 10%, 60%)' }}
            >
              skip
            </span>
          </motion.button>
        )}
      </AnimatePresence>
      
      {/* Scenes */}
      <AnimatePresence mode="wait">
        <Scene key={currentScene} scene={scenes[currentScene]}>
          <SceneContent 
            scene={scenes[currentScene]} 
            isFirst={currentScene === 0}
            hideContent={scenes[currentScene].isPathChoice && showPathChoice}
          />
        </Scene>
      </AnimatePresence>

      {/* Path choice on final scene - replaces scene content */}
      <AnimatePresence>
        {scenes[currentScene].isPathChoice && showPathChoice && !selectedPath && !showFeelInteractive && !showWeighInteractive && !showTranslateInteractive && (
          <PathChoice onSelect={handlePathSelect} />
        )}
      </AnimatePresence>

      {/* Feel Interactive Experience */}
      <AnimatePresence>
        {showFeelInteractive && (
          <FeelInteractive onExit={handleFeelExit} />
        )}
      </AnimatePresence>

      {/* Weigh Interactive Experience */}
      <AnimatePresence>
        {showWeighInteractive && (
          <WeighInteractive onExit={handleWeighExit} />
        )}
      </AnimatePresence>

      {/* Translate Interactive Experience */}
      <AnimatePresence>
        {showTranslateInteractive && (
          <TranslateInteractive onComplete={handleTranslateComplete} />
        )}
      </AnimatePresence>

      {/* Stabilize & Anchor Experience - automatic after Translate */}
      <AnimatePresence>
        {showStabilize && (
          <StabilizeAnchor onComplete={handleStabilizeComplete} />
        )}
      </AnimatePresence>

      {/* Room End Choice - appears at the end of Weigh, Translate */}
      <AnimatePresence>
        {scenes[currentScene].isRoomEnd && !showFeelInteractive && !showWeighInteractive && !showTranslateInteractive && !showStabilize && (
          <RoomEndChoice 
            options={scenes[currentScene].exitOptions} 
            onSelect={handleRoomEndSelect} 
          />
        )}
      </AnimatePresence>

      {/* Navigation hint - appears after delay, hidden in rooms, path choice, and closing */}
      <AnimatePresence>
        {showNavigation && !showFeelInteractive && !showWeighInteractive && !showTranslateInteractive && !showStabilize && !scenes[currentScene].isPathChoice && !scenes[currentScene].isRoomEnd && !scenes[currentScene].isClosingRitual && !scenes[currentScene].isFinalClose && (
          <NavigationHint 
            onNext={goToNextScene}
            onPrev={goToPrevScene}
            canGoNext={currentScene < scenes.length - 1 && !scenes[currentScene].isRoomEnd}
            canGoPrev={currentScene > 0 && !scenes[currentScene].isFeelRoom && !scenes[currentScene].isWeighRoom && !scenes[currentScene].isTranslateRoom && !scenes[currentScene].isClosingRitual}
            hasInteracted={hasInteracted}
          />
        )}
      </AnimatePresence>

      {/* Progress indicator - hidden in rooms and closing */}
      <AnimatePresence>
        {showNavigation && !showFeelInteractive && !showWeighInteractive && !showTranslateInteractive && !showStabilize && !scenes[currentScene].isFeelRoom && !scenes[currentScene].isWeighRoom && !scenes[currentScene].isTranslateRoom && !scenes[currentScene].isClosingRitual && !scenes[currentScene].isPathChoice && (
          <ProgressIndicator 
            current={currentScene}
            total={11} // Only show progress for main scenes (0-10)
            onSelect={goToScene}
            scenes={scenes.slice(0, 11)}
          />
        )}
      </AnimatePresence>
      </div>

      {/* Closing Screen - fade to black and stillness */}
      <AnimatePresence>
        {showClosing && (
          <ClosingScreen onRestart={handleClosingRestart} />
        )}
      </AnimatePresence>
    </>
  );
};

export default VaultenExperience;
